import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { QuizData, DataValues } from "../types/narrative";
import { replacePlaceholders } from "../utils/placeholders";
import { useUserInputContext } from "../hooks/useUserInputContext";
import { useSessionContext } from "../contexts/useSessionContext";

// Lottie confetti animation URL from LottieFiles
const CONFETTI_ANIMATION_URL = "/narratives/_shared/confetti.json";

interface QuizQuestionProps {
  quiz: QuizData;
  data: DataValues;
  quizId: string; // Unique ID for persisting answer state
  // Session action type to emit on answer. Omit for the default "quizClick",
  // provide a string to override it, or false to disable tracking.
  action?: string | false;
  onComplete: () => void;
  onAdvance?: () => void; // Called to advance to next content (like ReactionButtons)
}

type QuizState = "answering" | "correct" | "incorrect";

// Parse markdown bold (**text**) into styled spans
function parseMarkdownBold(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <span key={index} className="font-bold">
          {part.slice(2, -2)}
        </span>
      );
    }
    return part;
  });
}

export function QuizQuestion({
  quiz,
  data,
  quizId,
  action,
  onComplete,
  onAdvance,
}: QuizQuestionProps) {
  // Confetti is decorative. Under reduced motion, skip it entirely rather than
  // freezing on the last frame — confetti's final frame is effectively blank
  // (particles have fallen away), so there's no meaningful still to show.
  const prefersReducedMotion = useReducedMotion();
  const { getQuizAnswer, setQuizAnswer, isQuizCompleted } =
    useUserInputContext();
  const { recordAction } = useSessionContext();

  // Store callbacks in refs to avoid re-running effect when they change
  const onCompleteRef = useRef(onComplete);
  const onAdvanceRef = useRef(onAdvance);
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onAdvanceRef.current = onAdvance;
  }, [onComplete, onAdvance]);

  // Initialize from persisted state if quiz was previously answered correctly
  const previousAnswer = getQuizAnswer(quizId);
  const wasCompleted = isQuizCompleted(quizId);

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(
    wasCompleted ? (previousAnswer ?? null) : null,
  );
  const [quizState, setQuizState] = useState<QuizState>(
    wasCompleted ? "correct" : "answering",
  );
  const [showCelebration, setShowCelebration] = useState(false);

  // Process question and answers with placeholder replacement
  const processedQuestion = replacePlaceholders(quiz.question, data);
  const processedAnswers = quiz.answers.map((answer) =>
    replacePlaceholders(answer, data),
  );

  const handleAnswerSelect = (index: number) => {
    if (quizState !== "answering") return;

    setSelectedAnswer(index);

    const isCorrect = index === quiz.correctIndex;

    // Record the answer (correct or incorrect) by default as "quizClick"; a
    // custom action overrides the name, and action === false disables tracking.
    if (action !== false) {
      recordAction(typeof action === "string" ? action : "quizClick", {
        question: processedQuestion,
        clicked: index,
        answer: processedAnswers[index],
        correct: isCorrect,
      });
    }

    if (isCorrect) {
      setQuizState("correct");
      setShowCelebration(true);
      // Persist the correct answer to context
      setQuizAnswer(quizId, index);

      // Hide confetti after animation completes
      setTimeout(() => {
        setShowCelebration(false);
      }, 1200);

      // Mark quiz as complete (allows swiping) but don't auto-advance
      setTimeout(() => {
        onCompleteRef.current();
        // Note: Auto-advance removed - user should manually swipe to continue
      }, 1500);
    } else {
      setQuizState("incorrect");
      // Reset to answering state after a delay
      setTimeout(() => {
        setSelectedAnswer(null);
        setQuizState("answering");
      }, 1500);
    }
  };

  return (
    <div className="w-full space-y-6 relative">
      {/* Question */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-4">
          <span className="text-yellow-400 text-lg">🧠</span>
          <span className="text-white/80 text-sm font-medium">Quick Quiz</span>
        </div>
        <h2 className="text-2xl font-bold text-white leading-tight">
          {processedQuestion}
        </h2>
      </motion.div>

      {/* Answer options */}
      <div className="space-y-3">
        {processedAnswers.map((answer, index) => {
          const isSelected = selectedAnswer === index;
          const isCorrect = index === quiz.correctIndex;
          const showCorrectHighlight = quizState === "correct" && isCorrect;
          const showIncorrectHighlight =
            quizState === "incorrect" && isSelected && !isCorrect;

          return (
            <motion.button
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 + 0.3 }}
              onClick={() => handleAnswerSelect(index)}
              disabled={quizState !== "answering"}
              className={`w-full text-left py-4 px-5 rounded-2xl backdrop-blur-sm transition-all duration-300 cursor-pointer relative overflow-hidden ${
                showCorrectHighlight
                  ? "bg-green-500/30 border-2 border-green-400"
                  : showIncorrectHighlight
                    ? "bg-red-500/30 border-2 border-red-400 animate-shake"
                    : isSelected
                      ? "bg-white/25 border-2 border-white/50"
                      : "bg-white/10 border-2 border-transparent hover:bg-white/15 hover:border-white/20"
              }`}
            >
              {/* Letter indicator */}
              <div className="flex items-center gap-4">
                <motion.div
                  animate={{
                    scale: showCorrectHighlight ? [1, 1.2, 1] : 1,
                    backgroundColor: showCorrectHighlight
                      ? "rgb(34, 197, 94)"
                      : showIncorrectHighlight
                        ? "rgb(239, 68, 68)"
                        : "rgba(255, 255, 255, 0.2)",
                  }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg"
                >
                  {showCorrectHighlight ? (
                    <motion.svg
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 25,
                      }}
                      className="w-5 h-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </motion.svg>
                  ) : showIncorrectHighlight ? (
                    <motion.svg
                      initial={{ scale: 0, rotate: -90 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 25,
                      }}
                      className="w-5 h-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </motion.svg>
                  ) : (
                    <span className="text-white">
                      {String.fromCharCode(65 + index)}
                    </span>
                  )}
                </motion.div>

                <span className="text-lg text-white font-medium flex-1">
                  {parseMarkdownBold(answer)}
                </span>
              </div>

              {/* Shine effect for correct answer */}
              {showCorrectHighlight && (
                <motion.div
                  initial={{ x: "-100%", opacity: 0.5 }}
                  animate={{ x: "200%", opacity: 0 }}
                  transition={{ duration: 0.6 }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12"
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Celebration confetti - Lottie animation (skipped under reduced motion) */}
      {showCelebration &&
        !prefersReducedMotion &&
        createPortal(
          <AnimatePresence>
            <motion.div
              className="fixed inset-0 pointer-events-none z-[9999] flex items-center justify-center"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <DotLottieReact
                autoplay
                src={CONFETTI_ANIMATION_URL}
                style={{
                  width: "100vw",
                  height: "100vh",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </motion.div>
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}
